/**
 * Modal for creating a new car. POSTs /api/cars and (on success) navigates
 * the user straight to the new car's detail page.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";

import Modal from "../../components/Modal";
import CarIcon from "../../components/CarIcon";
import { ApiError } from "../../api/client";
import { useCreateCar } from "../../api/cars";
import type { CarIconName, FuelCategory } from "../../api/types";
import {
  CAR_ICONS,
  DEFAULT_FUEL_TYPE_BY_CATEGORY,
  FUEL_CATEGORIES,
  FUEL_TYPES_BY_CATEGORY,
  fuelTypeLabel,
} from "./carFormConstants";

interface AddCarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AddCarModal({ isOpen, onClose }: AddCarModalProps) {
  const navigate = useNavigate();
  const create = useCreateCar();

  const [name, setName] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [model, setModel] = useState("");
  const [icon, setIcon] = useState<CarIconName>("sedan");
  const [fuelCategory, setFuelCategory] = useState<FuelCategory>("gasoline");
  const [preferredFuel, setPreferredFuel] = useState<string>(
    DEFAULT_FUEL_TYPE_BY_CATEGORY.gasoline,
  );
  const [error, setError] = useState<string | null>(null);

  // Snap preferred fuel back to its default when category changes.
  useEffect(() => {
    setPreferredFuel(DEFAULT_FUEL_TYPE_BY_CATEGORY[fuelCategory]);
  }, [fuelCategory]);

  // Reset on open so a stale form never leaks across opens.
  useEffect(() => {
    if (isOpen) {
      setName("");
      setManufacturer("");
      setModel("");
      setIcon("sedan");
      setFuelCategory("gasoline");
      setPreferredFuel(DEFAULT_FUEL_TYPE_BY_CATEGORY.gasoline);
      setError(null);
    }
  }, [isOpen]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    try {
      const result = await create.mutateAsync({
        name: name.trim(),
        manufacturer: manufacturer.trim() || undefined,
        model: model.trim() || undefined,
        icon,
        fuel_category: fuelCategory,
        preferred_fuel_type: preferredFuel,
      });
      onClose();
      navigate(`/garage/${result.car.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create car.");
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add car">
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="field">
          <label className="field__label" htmlFor="add-car-name">
            Name
          </label>
          <input
            id="add-car-name"
            className="field__input"
            type="text"
            required
            maxLength={255}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="field-row">
          <div className="field field--grow">
            <label className="field__label" htmlFor="add-car-manufacturer">
              Manufacturer
            </label>
            <input
              id="add-car-manufacturer"
              className="field__input"
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
            />
          </div>
          <div className="field field--grow">
            <label className="field__label" htmlFor="add-car-model">
              Model
            </label>
            <input
              id="add-car-model"
              className="field__input"
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label className="field__label">Icon</label>
          <div className="icon-picker">
            {CAR_ICONS.map((opt) => (
              <button
                key={opt.name}
                type="button"
                aria-pressed={icon === opt.name}
                aria-label={opt.label}
                className={
                  "icon-picker__btn" +
                  (icon === opt.name ? " icon-picker__btn--active" : "")
                }
                onClick={() => setIcon(opt.name)}
              >
                <CarIcon name={opt.name} size={32} />
              </button>
            ))}
          </div>
        </div>

        <div className="field-row">
          <div className="field field--grow">
            <label className="field__label" htmlFor="add-car-category">
              Fuel category
            </label>
            <select
              id="add-car-category"
              className="field__input field__select"
              value={fuelCategory}
              onChange={(e) => setFuelCategory(e.target.value as FuelCategory)}
            >
              {FUEL_CATEGORIES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field field--grow">
            <label className="field__label" htmlFor="add-car-fuel">
              Preferred fuel
            </label>
            <select
              id="add-car-fuel"
              className="field__input field__select"
              value={preferredFuel}
              onChange={(e) => setPreferredFuel(e.target.value)}
            >
              {FUEL_TYPES_BY_CATEGORY[fuelCategory].map((ft) => (
                <option key={ft} value={ft}>
                  {fuelTypeLabel(ft)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && (
          <p className="form-status" data-variant="error" role="alert">
            {error}
          </p>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="button"
            onClick={onClose}
            disabled={create.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button button--primary"
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? "Saving…" : "Add car"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
